/**
 * What a list looks like while it is arriving.
 *
 * The idiom this replaces is the literal string `'Loading…'`, in about fourteen places. It is honest
 * but it makes the page jump: the text occupies one line, then the table arrives and everything below
 * it moves. A skeleton the shape of the real rows means the page is already the right size when the
 * data lands, so nothing under the operator's cursor moves at the moment they reach for it.
 *
 * Matched to the real geometry rather than being generic bars, which is the only version worth
 * having - a skeleton of the wrong shape is a second layout shift rather than none.
 *
 * `aria-hidden`, with the live region left to the caller. A screen reader should hear "loading cases"
 * once, not eight rows of nothing.
 */
export function TableSkeleton({ rows = 8, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="skeleton" aria-hidden="true" data-testid="loading-skeleton">
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="skeleton-row">
          {Array.from({ length: columns }, (_, column) => (
            // Varying widths, because a grid of identical bars reads as a rendering fault rather than
            // as a table arriving.
            <span key={column} className="skeleton-cell" style={{ width: `${45 + ((row * 7 + column * 23) % 45)}%` }} />
          ))}
        </div>
      ))}
    </div>
  )
}

/** The same idea for the Board's ribbon, where the shape is tiles rather than rows. */
export function TileSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="skeleton skeleton-tiles" aria-hidden="true" data-testid="loading-skeleton">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="skeleton-tile">
          <span className="skeleton-cell" style={{ width: '60%' }} />
          <span className="skeleton-cell skeleton-figure" />
        </div>
      ))}
    </div>
  )
}
